import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTeam, upsertTeamMember, deleteTeamMember } from '@/lib/api/team';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { refreshStore } from '@/lib/store';
import type { TeamMember as DbTeamMember } from '@/types/db';
import ImageUploadField from '@/components/base/ImageUploadField';

interface TeamMember {
  id: string;
  name: string;
  title: string;
  specialty: string;
  bio: string;
  certifications: string[];
  languages: string[];
  imageUrl: string;
  order: number;
}

function toVM(m: DbTeamMember): TeamMember {
  return {
    id: m.id,
    name: m.name,
    title: m.title ?? '',
    specialty: m.specialty ?? '',
    bio: m.bio ?? '',
    certifications: m.certifications ?? [],
    languages: m.languages ?? [],
    imageUrl: m.image_url ?? '',
    order: m.sort_order ?? 0,
  };
}

function toDb(m: TeamMember): Partial<DbTeamMember> & { id: string } {
  return {
    id: m.id,
    name: m.name,
    title: m.title,
    specialty: m.specialty,
    bio: m.bio,
    certifications: m.certifications,
    languages: m.languages,
    image_url: m.imageUrl,
    sort_order: m.order,
    visible: true,
  };
}


export default function TeamAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadMembers();
  }, [navigate]);

  const loadMembers = async () => {
    try {
      const data = await getTeam();
      setMembers(data.map(toVM));
    } catch {
      setMembers([]);
    }
  };

  const afterWrite = async (detail: string) => {
    await logActivityDb('team_updated', detail);
    await refreshStore();
    await loadMembers();
    setSavedMsg(t('adm1.saved'));
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const handleAddNew = () => {
    const slug = `tm-${Date.now()}`;
    setEditing({
      id: slug,
      name: '',
      title: '',
      specialty: '',
      bio: '',
      certifications: [],
      languages: [],
      imageUrl: '',
      order: members.length + 1,
    });
    setShowModal(true);
  };

  const handleEdit = (m: TeamMember) => {
    setEditing({ ...m });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      await upsertTeamMember(toDb(editing));
      await afterWrite('Teammitglied gespeichert');
    } catch {
      setSavedMsg(t('adm1.save_error'));
    }
    setShowModal(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTeamMember(id);
      await afterWrite('Teammitglied gelöscht');
    } catch { /* ignore */ }
    setDeleteConfirm(null);
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.team_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">{t('adm1.team_count', { count: members.length })}</p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('adm1.team_new')}
          </button>
        </div>
      </div>

      {/* ── Team Grid ──────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {members
          .sort((a, b) => a.order - b.order)
          .map((member) => (
            <div key={member.id} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group">
              <div className="aspect-square bg-background-100 overflow-hidden">
                <img
                  src={member.imageUrl || 'https://readdy.ai/api/search-image?query=Professional%20medical%20professional%20portrait%20placeholder%2C%20neutral%20background%2C%20clean%20minimal%20style&width=400&height=400&seq=team-placeholder&orientation=squarish'}
                  alt={member.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4">
                <h3 className="font-label text-sm font-semibold text-foreground-900">{member.name}</h3>
                <p className="text-xs text-primary-600 font-label mt-0.5">{member.title}</p>
                <p className="text-[10px] text-foreground-400 font-body mt-0.5">{member.specialty}</p>
                <p className="text-[11px] text-foreground-500 font-body mt-2 line-clamp-2">{member.bio}</p>
                <div className="flex items-center gap-1 mt-2 flex-wrap">
                  {member.languages.map((lang) => (
                    <span key={lang} className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 border border-background-200 text-foreground-500 font-label whitespace-nowrap">
                      {lang}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-1 mt-3">
                  <button
                    onClick={() => handleEdit(member)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                    title={t('adm1.action_edit')}
                  >
                    <i className="ri-edit-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(member.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                    title={t('adm1.action_delete')}
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* ── Edit Modal ──────────────────────────────── */}
      {showModal && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-background-100 flex items-center justify-between">
              <h2 className="font-label text-sm font-semibold text-foreground-900">
                {editing.name ? t('adm1.team_edit') : t('adm1.team_new')}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditing(null); }}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_name')}</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_title_role')}</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder={t('adm1.ph_title_role')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_specialty')}</label>
                <input
                  type="text"
                  value={editing.specialty}
                  onChange={(e) => setEditing({ ...editing, specialty: e.target.value })}
                  placeholder={t('adm1.ph_specialty')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_bio')}</label>
                <textarea
                  value={editing.bio}
                  onChange={(e) => setEditing({ ...editing, bio: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_certifications')}</label>
                <input
                  type="text"
                  value={editing.certifications.join(', ')}
                  onChange={(e) => setEditing({ ...editing, certifications: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  placeholder={t('adm1.ph_certifications')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_languages')}</label>
                <input
                  type="text"
                  value={editing.languages.join(', ')}
                  onChange={(e) => setEditing({ ...editing, languages: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  placeholder={t('admx.team_languages_placeholder')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
              <ImageUploadField
                label={t('adm1.label_photo')}
                value={editing.imageUrl}
                onChange={(url) => setEditing({ ...editing, imageUrl: url })}
              />
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_order')}</label>
                <input
                  type="number"
                  value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 1 })}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-background-100 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowModal(false); setEditing(null); }}
                className="px-4 py-2 rounded-lg text-xs font-label text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ──────────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 mx-auto mb-3">
              <i className="ri-error-warning-line text-lg"></i>
            </div>
            <h3 className="text-center font-label text-sm font-semibold text-foreground-900">{t('adm1.team_delete_confirm')}</h3>
            <p className="mt-1 text-center text-xs text-foreground-500 font-body">{t('adm1.delete_irreversible')}</p>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 rounded-lg border border-background-200 text-xs font-label text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.cancel')}</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-xs font-label hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}